import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RobotFamilyMetadata } from 'src/app/models/robot-family-metadata';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { SharedModule } from '../../../shared/shared.module';
import { DeleteMetadata, LoadMetadata, ReloadMetadata, RobotFamilyMetadataState } from '../redux';
import { EditRobotFamilyMetadataComponent } from './edit-robot-family-metadata/edit-robot-family-metadata.component';

@Component({
  selector: 'app-robot-family-metadata',
  templateUrl: './robot-family-metadata.component.html',
  styleUrls: ['./robot-family-metadata.component.css'],
  providers: [DecimalPipe],
  imports: [SharedModule, RouterModule, RouterOutlet],
  standalone: true
})
export class RobotFamilyMetadataComponent implements OnInit, OnDestroy {
  public robotFamilyMetadata$: Observable<RobotFamilyMetadata[]> = inject(Store).select(
    RobotFamilyMetadataState.robotFamilyMetadata
  );
  private unsubscribe = new Subject<void>();

  public tableData?: RobotFamilyMetadata[];
  public showActions = [ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.CLONE, ACTIONS.DELETE];

  constructor(
    private store: Store,
    private dialogService: NbDialogService,
    private router: Router
  ) {
    if (!this.store.selectSnapshot(RobotFamilyMetadataState.robotFamilyMetadata)) {
      this.store.dispatch(LoadMetadata);
    }
  }

  public ngOnInit(): void {
    this.robotFamilyMetadata$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadMetadata);
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditRobotFamilyMetadataComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          robotFamilyMetadata: new RobotFamilyMetadata(),
          allRFM: this.tableData
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const metadata: RobotFamilyMetadata = $event.data;
      const clonedMetadata = RobotFamilyMetadata.clone(metadata);
      clonedMetadata.robot_family = 'Clone-' + clonedMetadata.robot_family;
      this.dialogService.open(EditRobotFamilyMetadataComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          robotFamilyMetadata: clonedMetadata,
          allRFM: this.tableData
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const robotfamilymetadata: RobotFamilyMetadata = $event.data;
      this.router.navigate([`robot-family-metadata/table/` + robotfamilymetadata.robot_family]);
    }
    if ($event.action === ACTIONS.EDIT) {
      const metadata: RobotFamilyMetadata = $event.data;
      this.dialogService.open(EditRobotFamilyMetadataComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          robotFamilyMetadata: RobotFamilyMetadata.copy(metadata),
          allRFM: this.tableData
        }
      });
    }
    if ($event.action === ACTIONS.DELETE) {
      const metadata: RobotFamilyMetadata = $event.data;
      this.dialogService
        .open(NgxGenericModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Are you sure you want to delete Robot Family Metadata`,
            message: metadata.robot_family + ' - ' + metadata.metadata_type
          }
        })
        .onClose.subscribe((data) => {
          if (data === true) {
            const metadataToDelete = RobotFamilyMetadata.copy(metadata);
            metadataToDelete.deleted = true;
            this.store.dispatch(new DeleteMetadata(metadataToDelete));
          }
        });
    }
  }
}
